/**
 * Brand pages (`/maerke/<slug>` on the storefront).
 *
 * The slug is the identity everywhere — `brands.id` is regenerated every night by the `run_brands`
 * rollup, so nothing in the admin links, filters or patches by it.
 */

/** Mirrors INDEXABLE_COMPARABLE in the admin server, run_brands.rs and the storefront brand page. */
export const INDEXABLE_COMPARABLE = 20;

export const STOREFRONT_URL = "https://www.priceonline.dk";

export const brandPageUrl = (slug: string) =>
	`${STOREFRONT_URL}/maerke/${encodeURIComponent(slug)}`;

export interface BrandListItem {
	slug: string;
	name: string;
	/** The rollup's own name, differs from `name` only when a display name overrides it. */
	rollupName: string;
	productCount: number;
	comparableCount: number;
	indexable: boolean;
	hidden: boolean;
	mergedInto: string | null;
	suspectMerchantName: boolean;
	topCategoryId: number | null;
	rootCategoryId: number | null;
	rootCategoryName: string | null;
	aliasCount: number;
	normKey: string;
	hasOverride: boolean;
	note: string | null;
	updatedBy: string | null;
	updatedAt: string | null;
}

export interface BrandsResponse {
	items: BrandListItem[];
	total: number;
	page: number;
	totalPages: number;
}

export interface BrandStats {
	total: number;
	indexable: number;
	hidden: number;
	merged: number;
	suspect: number;
	aliasCollisions: number;
	withOverrides: number;
	lastRebuild: string | null;
}

export interface BrandAlias {
	value: string;
	productCount: number;
}

export interface BrandCategory {
	categoryId: number;
	name: string;
	productCount: number;
}

export interface BrandSampleProduct {
	id: number;
	name: string;
	url: string | null;
	image: string | null;
	offerCount: number;
}

export interface BrandMerchant {
	id: number;
	name: string;
	productCount: number;
}

export interface BrandSimilar {
	slug: string;
	name: string;
	productCount: number;
	comparableCount: number;
}

export interface BrandSearchConsole {
	days: number;
	impressions: number;
	clicks: number;
	avgPosition: number | null;
	/** Distinct brand URLs that drew at least one impression — the ones Google is actually serving. */
	urlsWithImpressions: number;
	firstDay: string | null;
	lastDay: string | null;
}

export interface BrandDetail extends BrandListItem {
	description: string | null;
	aliases: BrandAlias[];
	categories: BrandCategory[];
	sampleProducts: BrandSampleProduct[];
	merchants: BrandMerchant[];
	similar: BrandSimilar[];
	overrideUpdatedAt: string | null;
	searchConsole: BrandSearchConsole;
}

export interface BrandOverridePatch {
	displayName?: string | null;
	description?: string | null;
	hidden?: boolean;
	mergeInto?: string | null;
	note?: string | null;
}

export type BrandSort = "comparable" | "products" | "name" | "aliases";
